import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Handles the "Add" action for a Smart Cross-Sell card.
 *
 * Mirrors cart-rewards.js's addGift() exactly: dispatches the theme's
 * existing CartLinesUpdateEvent so the ancestor `cart-items-component`
 * morphs itself from the returned sections - this component does not morph
 * anything itself, and there is no separate fetch/morph logic to maintain.
 *
 * @typedef {Object} CartCrossSellRefs
 * @property {HTMLElement} feedback - The aria-live status element for add feedback/errors.
 *
 * @extends {Component<CartCrossSellRefs>}
 */
class CartCrossSell extends Component {
  requiredRefs = ['feedback'];

  /**
   * Adds the clicked card's variant to the cart.
   * @param {MouseEvent} event
   */
  addProduct = async (event) => {
    event.preventDefault();

    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const { variantId } = button.dataset;
    if (!variantId) return;

    const cartItemsComponent = this.closest('cart-items-component');
    if (!(cartItemsComponent instanceof HTMLElement) || !cartItemsComponent.dataset.sectionId) return;

    const sectionId = cartItemsComponent.dataset.sectionId;

    button.disabled = true;

    const deferredPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart-cross-sell',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: [sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_add_url, config);
      const data = await response.json();

      if (data.status) {
        const message = typeof data.description === 'string' ? data.description : data.message;
        this.refs.feedback.textContent = message || '';
        this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        deferredPromise.reject(new Error(message));
        button.disabled = false;
        return;
      }

      button.dataset.added = 'true';
      this.refs.feedback.textContent = button.dataset.addedLabel || '';
      window.setTimeout(() => {
        delete button.dataset.added;
        button.disabled = false;
      }, 1600);

      // The item count is already in this response's own section HTML (the
      // same `[ref="cartItemCount"]` element component-cart-items.js reads
      // from a single response) - read it from there instead of always
      // paying a second /cart.json round-trip just for the header badge.
      const countText = data.sections?.[sectionId]
        ? new DOMParser().parseFromString(data.sections[sectionId], 'text/html').querySelector('[ref="cartItemCount"]')
            ?.textContent
        : undefined;
      const fastItemCount = countText ? parseInt(countText, 10) : undefined;

      if (fastItemCount !== undefined) {
        deferredPromise.resolve({
          cart: {
            id: '',
            totalQuantity: fastItemCount,
            cost: { totalAmount: { amount: '', currencyCode: '' } },
            lines: [],
            discountCodes: [],
          },
          detail: { sections: data.sections },
        });
      } else {
        /** @type {import('@theme/component-cart-items').Cart | null} */
        let freshCart = null;
        try {
          freshCart = await /** @type {import('@theme/component-cart-items').CartItemsComponent} */ (
            cartItemsComponent
          ).fetchCartData();
        } catch (fetchCartError) {
          console.warn('[cart-cross-sell] Failed to refresh cart after adding product', fetchCartError);
        }

        deferredPromise.resolve({
          cart: freshCart ? CartLinesUpdateEvent.createCartFromAjaxResponse(freshCart) : null,
          detail: { sections: data.sections },
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.refs.feedback.textContent = message;
      deferredPromise.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: message, code: 'SERVICE_UNAVAILABLE' }));
      button.disabled = false;
    }
  };
}

if (!customElements.get('cart-cross-sell-component')) {
  customElements.define('cart-cross-sell-component', CartCrossSell);
}
